export const BB1_ANGULAR_RATE_KEYS = [
    "RWA_YM0_Z_MEASSPEED",
    "RWA_YM1_X_MEASSPEED",
    "RWA_YP0_Z_MEASSPEED",
    "RWA_YP1_X_MEASSPEED",
];

export const BB2_ANGULAR_RATE_KEYS = [
    "RWA_01_SPEED_MEAS_RPM",
    "RWA_02_SPEED_MEAS_RPM",
    "RWA_03_SPEED_MEAS_RPM",
    "RWA_04_SPEED_MEAS_RPM",
    "RWA_05_SPEED_MEAS_RPM",
    "RWA_06_SPEED_MEAS_RPM",
    "RWA_07_SPEED_MEAS_RPM",
    "RWA_08_SPEED_MEAS_RPM",
    "RWA_09_SPEED_MEAS_RPM",
    "RWA_10_SPEED_MEAS_RPM",
    "RWA_11_SPEED_MEAS_RPM",
    "RWA_12_SPEED_MEAS_RPM",
];

// limit definitions
// BLOCK 1
export const ANGULAR_RATE_GREEN_HIGH = 4000;
export const ANGULAR_RATE_YELLOW_HIGH = 5500;

export const GREEN_SC_MODE = [
    "THERMAL_ROLL", "DEPLOYED_COAST", "PLANK_NADIR",
];
export const GREEN_AOCS_ACMODE_ACTIVE = [
    "ADCS_MODE_THERMALROLL", "ADCS_MODE_NADIRPOINT", "ADCS_MODE_PLANKNADIR",
];

export const BATT_GREEN_LOW = 30;
export const BATT_GREEN_HIGH = 75;
export const BATT_YELLOW_LOW = 15;
export const BATT_YELLOW_HIGH = 95;

// BLOCK 2
export const ANGULAR_RATE_GREEN_HIGH_BB2 = 2000;
export const ANGULAR_RATE_YELLOW_HIGH_BB2 = 2500;

export const GREEN_SC_MODE_BB2 = [
    "THERMAL_ROLL", "DEPLOYED_COAST", "PLANK_NADIR",
];
export const GREEN_AOCS_ACMODE_ACTIVE_BB2 = [
    "ADCS_MODE_THERMALROLL", "ADCS_MODE_NADIRPOINT", "ADCS_MODE_PLANKNADIR",
    "ADCS_MODE_SUNTARGETSTEERING",
];


/**
 * Analyzes the Attitude and Orbit Control System (AOCS) status based on the provided data.
 *
 * The AOCS status is determined by analyzing the various components of the AOCS, such as angular rates,
 * mode switching, and active attitude control modes. A color code is returned to indicate the overall
 * health of the AOCS: 0 = red (critical), 1 = yellow (warning), or 2 = green (normal).
 *
 * @param {object} data - An object containing key-value pairs representing different aspects of the AOCS.
 *                         Each value should be a string that can be analyzed by one of the supported analysis functions.
 * @param {object} isBB2 - Is Block 2 sat (SM006)
 *
 * @return {number} A color code indicating the overall status of the AOCS: 0 = red, 1 = yellow, or 2 = green.
 */
export function aocsStatus(data, isBB2=false) {
    const statuses = {};
    if (!data || !data.length) {
        return {
            overall: 3,
            individual: statuses,
        };
    }

    data.forEach((point) => {
        let status = null;

        if (point.tlm_name === "SC_MODE") {
            status = analyzeModeSwitching(point.value, isBB2);
        } else if (point.tlm_name === "AOCS_ACMODE_ACTIVE") {
            status = analyzeACModeActive(point.value, isBB2);
        } else if (
            (!isBB2 && BB1_ANGULAR_RATE_KEYS.includes(point.tlm_name))
            || (isBB2 && BB2_ANGULAR_RATE_KEYS.includes(point.tlm_name))
        ) {
            status = analyzeAngularRate(point.value, isBB2);
        }

        if (status !== null) {
            statuses[point.tlm_name] = {
                status,
                value: point.value,
            };
        }
    });

    const worstStatus = Math.min(...Object.values(statuses).map((s) => s.status));
    return {
        overall: worstStatus,
        individual: statuses,
    };
}

/**
   * Analyzes the measured angular rate from HW.
   *
   * @param {object} value - The data object to analyze.
   * @param {boolean} isBB2 - Whether sat is Block 2
   * @return {number} A color code (0 = red, 1 = yellow, 2 = green)
   */
function analyzeAngularRate(value, isBB2) {
    const greenHigh = isBB2 ? ANGULAR_RATE_GREEN_HIGH_BB2 : ANGULAR_RATE_GREEN_HIGH;
    const yellowHigh = isBB2 ? ANGULAR_RATE_YELLOW_HIGH_BB2 : ANGULAR_RATE_YELLOW_HIGH;

    value = Math.abs(parseFloat(value));
    if (value < greenHigh) return 2; // Green
    else if (value < yellowHigh) return 1; // Yellow
    else return 0; // Red
}

/**
   * Analyzes the mode switching.
   *
   * @param {object} value - The data object to analyze.
   * @param {boolean} isBB2 - Whether sat is Block 2
   * @return {number} A color code (0 = red, 1 = yellow, 2 = green)
   */
function analyzeModeSwitching(value, isBB2) {
    const greenModes = isBB2 ? GREEN_SC_MODE_BB2 : GREEN_SC_MODE;
    if (greenModes.includes(value)) {
        return 2; // Green
    } else {
        return 1; // Yellow
    }
}

/**
   * Analyzes the current attitude control mode.
   *
   * @param {object} value - The data object to analyze.
   * @param {boolean} isBB2 - Whether sat is Block 2
   * @return {number} A color code (0 = red, 1 = yellow, 2 = green)
   */
function analyzeACModeActive(value, isBB2) {
    const greenModes = isBB2 ? GREEN_AOCS_ACMODE_ACTIVE_BB2 : GREEN_AOCS_ACMODE_ACTIVE;
    if (greenModes.includes(value)) {
        return 2; // Green
    } else {
        return 1; // Yellow
    }
}

export function epsStatus(name, value, isBB2) {
    let status = 3; // not loaded

    if (BATT_GREEN_LOW < value && value < BATT_GREEN_HIGH) {
        status = 2; // Green
    } else if (BATT_YELLOW_LOW < value && value < BATT_YELLOW_HIGH) {
        status = 1; // Yellow
    } else {
        status = 0; // Red
    }

    const individual = {};
    individual[name] = {
        status: status,
        value: value,
    };

    return {
        overall: status,
        individual: individual,
    };
}

const getPrimaryStack = (stackLocation, stackState) => {
    // if YM is the secondary, YP is the primary
    if ((stackLocation === "YP" && stackState === "PRIMARY") || (stackLocation === "YM" && stackState === "SECONDARY")) {
        return "YP";
    }
    if ((stackLocation === "YM" && stackState === "PRIMARY") || (stackLocation === "YP" && stackState === "SECONDARY")) {
        return "YM";
    }
    if ((stackLocation === "A" && stackState === "PRIMARY") || (stackLocation === "B" && stackState === "SECONDARY")) {
        return "A";
    }
    if ((stackLocation === "B" && stackState === "PRIMARY") || (stackLocation === "A" && stackState === "SECONDARY")) {
        return "B";
    }
    return "N/A";
};

export function medicStackStatus(medicStackLocation, medicStackState) {
    const statuses = {};
    const primaryStack = getPrimaryStack(medicStackLocation, medicStackState);

    const green = primaryStack === "YP" || primaryStack === "A";

    statuses["MEDIC_STACK_LOCATION"] = {
        status: !medicStackLocation ? 3 : green,
        value: medicStackLocation,
    };

    statuses["MEDIC_STACK_STATE"] = {
        status: !medicStackState ? 3 : green,
        value: medicStackState,
    };

    let status = 3; // not loaded
    if (medicStackState) {
        status = green ? 2 : 0; // Green or red
    }

    return {
        overall: status,
        individual: statuses,
        displayValue: primaryStack,
    };
}
